import * as i6 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Renderer2, Component, ChangeDetectionStrategy, ElementRef, Directive, Inject, EventEmitter, ChangeDetectorRef, Optional, Self, ViewChild, Input, Output, NgModule } from '@angular/core';
import * as i10 from '@angular/forms';
import { NgControl } from '@angular/forms';
import * as i1 from '@taiga-ui/addon-editor/components/toolbar';
import { TuiToolbarComponent, TuiToolbarModule } from '@taiga-ui/addon-editor/components/toolbar';
import { defaultEditorTools } from '@taiga-ui/addon-editor/constants';
import * as i9 from '@taiga-ui/addon-editor/directives';
import { TuiTiptapEditorService, TuiTiptapEditorDirective, TuiTiptapEditorModule } from '@taiga-ui/addon-editor/directives';
import { LAZY_EDITOR_EXTENSIONS, TUI_EDITOR_EXTENSIONS, INITIALIZATION_TIPTAP_CONTAINER, TIPTAP_EDITOR, LAZY_TIPTAP_EDITOR, TUI_EDITOR_CONTENT_PROCESSOR, TUI_EDITOR_VALUE_TRANSFORMER, TUI_EDITOR_OPTIONS } from '@taiga-ui/addon-editor/tokens';
import { tuiIsSafeLinkRange } from '@taiga-ui/addon-editor/utils';
import * as i7 from '@taiga-ui/cdk';
import { AbstractTuiPortalService, AbstractTuiPortalHostComponent, TuiDropdownPortalService, AbstractTuiControl, ALWAYS_FALSE_HANDLER, tuiAsFocusableItemAccessor, tuiAutoFocusOptionsProvider, TuiActiveZoneModule, TuiItemDirective } from '@taiga-ui/cdk';
import * as i2 from '@taiga-ui/core';
import { TuiRectAccessor, tuiAsViewport, TUI_ANIMATIONS_DEFAULT_DURATION, TuiWrapperModule, TuiScrollbarModule, TuiDropdownModule } from '@taiga-ui/core';
import { take, map, shareReplay, delay, takeUntil } from 'rxjs/operators';
import * as i11 from 'rxjs';
import { ReplaySubject, combineLatest } from 'rxjs';
import * as i3 from '@taiga-ui/addon-editor/components/editor-socket';
import { TuiEditorSocketModule } from '@taiga-ui/addon-editor/components/editor-socket';
import * as i4 from '@taiga-ui/addon-editor/components/edit-link';
import { TuiEditLinkModule } from '@taiga-ui/addon-editor/components/edit-link';
import * as i12 from '@taiga-ui/addon-editor/abstract';

class TuiEditorPortalService extends AbstractTuiPortalService {
}
TuiEditorPortalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
TuiEditorPortalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalService, decorators: [{
            type: Injectable
        }] });

const TUI_EDITOR_PROVIDERS = [
    {
        provide: LAZY_EDITOR_EXTENSIONS,
        deps: [TUI_EDITOR_EXTENSIONS],
        useFactory: (extensions) => {
            const extensions$ = new ReplaySubject(1);
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            Promise.all(extensions).then(extensions => extensions$.next(extensions));
            return extensions$;
        },
    },
    {
        provide: INITIALIZATION_TIPTAP_CONTAINER,
        deps: [Renderer2],
        useFactory: (renderer) => renderer.createElement(`div`),
    },
    {
        provide: TIPTAP_EDITOR,
        deps: [
            INITIALIZATION_TIPTAP_CONTAINER,
            LAZY_EDITOR_EXTENSIONS,
            LAZY_TIPTAP_EDITOR,
        ],
        useFactory: (element, extensions, editor) => {
            return combineLatest([editor, extensions]).pipe(take(1), map(([LazyEditor, extensions]) => new LazyEditor({
                element,
                extensions,
            })), shareReplay({ bufferSize: 1, refCount: true }));
        },
    },
    TuiTiptapEditorService,
    TuiEditorPortalService,
];

class TuiEditorPortalHostComponent extends AbstractTuiPortalHostComponent {
}
TuiEditorPortalHostComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalHostComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
TuiEditorPortalHostComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiEditorPortalHostComponent, selector: "tui-editor-portal-host", providers: [
        { provide: AbstractTuiPortalService, useExisting: TuiDropdownPortalService },
        {
            provide: AbstractTuiPortalHostComponent,
            useExisting: TuiEditorPortalHostComponent,
        },
    ], usesInheritance: true, ngImport: i0, template: `
        <ng-container #viewContainer></ng-container>
    `, isInline: true, styles: [":host{position:relative;z-index:1;display:block;height:100%}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-editor-portal-host',
                    template: `
        <ng-container #viewContainer></ng-container>
    `,
                    styleUrls: ['./editor-portal-host.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [
                        { provide: AbstractTuiPortalService, useExisting: TuiDropdownPortalService },
                        {
                            provide: AbstractTuiPortalHostComponent,
                            useExisting: TuiEditorPortalHostComponent,
                        },
                    ],
                }]
        }] });

class TuiEditorPortalDirective extends TuiRectAccessor {
    constructor(el) {
        super();
        this.el = el;
        this.type = 'viewport';
    }
    getClientRect() {
        return this.el.nativeElement.getBoundingClientRect();
    }
}
TuiEditorPortalDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalDirective, deps: [{ token: ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
TuiEditorPortalDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: TuiEditorPortalDirective, selector: "[tuiEditorPortal]", providers: [
        { provide: TuiDropdownPortalService, useExisting: TuiEditorPortalService },
        tuiAsViewport(TuiEditorPortalDirective),
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorPortalDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[tuiEditorPortal]',
                    providers: [
                        { provide: TuiDropdownPortalService, useExisting: TuiEditorPortalService },
                        tuiAsViewport(TuiEditorPortalDirective),
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }]; } });

class TuiEditorComponent extends AbstractTuiControl {
    constructor(control, cdr, editorLoaded$, editorService, contentProcessor, doc, transformer, options) {
        super(control, cdr, transformer);
        this.editorLoaded$ = editorLoaded$;
        this.editorService = editorService;
        this.contentProcessor = contentProcessor;
        this.doc = doc;
        this.options = options;
        this.exampleText = '';
        this.tools = defaultEditorTools;
        this.fileAttached = new EventEmitter();
        this.focused = false;
        this.isSelectionLink = (range) => this.currentFocusedNodeIsTextAnchor(range) ||
            this.currentFocusedNodeIsImageAnchor;
        this.editorLoaded$
            .pipe(delay(0), takeUntil(this.destroy$))
            .subscribe(() => this.patchContentEditableElement());
    }
    get nativeFocusableElement() {
        var _a, _b;
        return this.computedDisabled
            ? null
            : ((_b = (_a = this.el) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.querySelector('[contenteditable].ProseMirror')) ||
                null;
    }
    get dropdownSelectionHandler() {
        return this.focused ? this.isSelectionLink : ALWAYS_FALSE_HANDLER;
    }
    get editor() {
        return this.editorService.getOriginTiptapEditor() ? this.editorService : null;
    }
    get placeholderRaised() {
        return (this.computedFocused && !this.readOnly) || this.hasValue;
    }
    get hasExampleText() {
        return (!!this.exampleText && this.computedFocused && !this.hasValue && !this.readOnly);
    }
    writeValue(value) {
        var _a;
        if (value === this.value) {
            return;
        }
        const processed = this.contentProcessor(value || '');
        super.writeValue(processed);
        if (processed !== value) {
            (_a = this.control) === null || _a === void 0 ? void 0 : _a.setValue(processed, {
                onlySelf: false,
                emitEvent: false,
                emitModelToViewChange: false,
                emitViewToModelChange: false,
            });
        }
    }
    onActiveZone(focused) {
        var _a;
        this.focused = focused;
        this.updateFocused(focused);
        (_a = this.control) === null || _a === void 0 ? void 0 : _a.updateValueAndValidity();
    }
    onModelChange(value) {
        this.value = value;
    }
    addAnchor(anchor) {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.setAnchor(anchor);
    }
    removeAnchor() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.removeAnchor();
    }
    addLink(link) {
        var _a, _b;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.selectClosest();
        (_b = this.editor) === null || _b === void 0 ? void 0 : _b.setLink(link);
    }
    removeLink() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.unsetLink();
    }
    focus(event) {
        var _a, _b;
        if ((_a = this.nativeFocusableElement) === null || _a === void 0 ? void 0 : _a.contains(event.target)) {
            return;
        }
        event.preventDefault();
        (_b = this.nativeFocusableElement) === null || _b === void 0 ? void 0 : _b.focus();
    }
    ngOnDestroy() {
        var _a;
        (_a = this.editor) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    getFallbackValue() {
        return '';
    }
    /**
     * @description:
     * The commonAncestorContainer not always relevant node element in Range,
     * so the focusNode is used for the correct behaviour from the selection,
     * which is the actual element at the moment
     */
    currentFocusedNodeIsTextAnchor(range) {
        var _a, _b;
        return (!!((_b = (_a = range.startContainer.parentElement) === null || _a === void 0 ? void 0 : _a.closest('a')) === null || _b === void 0 ? void 0 : _b.contains(this.focusNode)) && tuiIsSafeLinkRange(range));
    }
    get focusNode() {
        var _a, _b;
        return (_b = (_a = this.doc.getSelection()) === null || _a === void 0 ? void 0 : _a.focusNode) !== null && _b !== void 0 ? _b : null;
    }
    get hasValue() {
        return !!this.value;
    }
    get currentFocusedNodeIsImageAnchor() {
        var _a, _b, _c;
        return (((_a = this.focusNode) === null || _a === void 0 ? void 0 : _a.nodeName) === 'A' &&
            ['IMG', 'TUI-IMAGE-EDITOR'].includes((_c = (_b = this.focusNode) === null || _b === void 0 ? void 0 : _b.childNodes[0]) === null || _c === void 0 ? void 0 : _c.nodeName));
    }
    patchContentEditableElement() {
        var _a, _b;
        (_a = this.nativeFocusableElement) === null || _a === void 0 ? void 0 : _a.setAttribute('translate', this.options.translate);
        (_b = this.nativeFocusableElement) === null || _b === void 0 ? void 0 : _b.setAttribute('spellcheck', String(this.options.spellcheck));
    }
}
TuiEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorComponent, deps: [{ token: NgControl, optional: true, self: true }, { token: ChangeDetectorRef }, { token: TIPTAP_EDITOR }, { token: TuiTiptapEditorService }, { token: TUI_EDITOR_CONTENT_PROCESSOR }, { token: DOCUMENT }, { token: TUI_EDITOR_VALUE_TRANSFORMER, optional: true }, { token: TUI_EDITOR_OPTIONS }], target: i0.ɵɵFactoryTarget.Component });
TuiEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiEditorComponent, selector: "tui-editor", inputs: { exampleText: "exampleText", tools: "tools" }, outputs: { fileAttached: "fileAttached" }, providers: [
        tuiAsFocusableItemAccessor(TuiEditorComponent),
        tuiAutoFocusOptionsProvider({ delay: TUI_ANIMATIONS_DEFAULT_DURATION }),
        TUI_EDITOR_PROVIDERS,
    ], viewQueries: [{ propertyName: "el", first: true, predicate: TuiTiptapEditorDirective, descendants: true, read: ElementRef }, { propertyName: "toolbar", first: true, predicate: TuiToolbarComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\n    *ngIf=\"editorLoaded$ | async as editorLoaded\"\n    tuiWrapper\n    appearance=\"textfield\"\n    class=\"t-wrapper\"\n    [readOnly]=\"readOnly\"\n    [disabled]=\"disabled\"\n    [focus]=\"computedFocused\"\n    [hover]=\"pseudoHover\"\n    [invalid]=\"computedInvalid\"\n    (tuiActiveZoneChange)=\"onActiveZone($event)\"\n>\n    <div class=\"t-toolbar-wrapper\">\n        <tui-toolbar\n            *ngIf=\"editorLoaded\"\n            [tools]=\"tools\"\n            [disabled]=\"!interactive\"\n            (fileAttached)=\"fileAttached.emit($event)\"\n        >\n            <ng-content select=\"tools\"></ng-content>\n        </tui-toolbar>\n        <div\n            class=\"t-placeholder\"\n            [class.t-placeholder_raised]=\"placeholderRaised\"\n        >\n            <ng-content></ng-content>\n        </div>\n        <div\n            *ngIf=\"hasExampleText\"\n            class=\"t-example-text\"\n        >\n            {{ exampleText }}\n        </div>\n    </div>\n\n    <tui-scrollbar\n        *ngIf=\"editorLoaded\"\n        tuiEditorPortal\n        class=\"t-scrollbar\"\n        (mousedown)=\"focus($event)\"\n    >\n        <div\n            tuiDropdownLimitWidth=\"auto\"\n            tuiDropdownSelectionPosition=\"tag\"\n            [tuiDropdownSelection]=\"dropdownSelectionHandler\"\n            [tuiDropdown]=\"dropdown\"\n        >\n            <tui-editor-socket\n                tuiTiptapEditor\n                class=\"tui-editor-socket\"\n                [value]=\"value\"\n                [editable]=\"interactive\"\n                (valueChange)=\"onModelChange($event)\"\n                (stateChange)=\"(0)\"\n            ></tui-editor-socket>\n        </div>\n\n        <ng-template #dropdown>\n            <tui-edit-link\n                #link\n                (addLink)=\"link.anchorMode ? addAnchor($event) : addLink($event)\"\n                (removeLink)=\"link.anchorMode ? removeAnchor() : removeLink()\"\n            ></tui-edit-link>\n        </ng-template>\n\n        <tui-editor-portal-host></tui-editor-portal-host>\n    </tui-scrollbar>\n</div>\n", styles: [":host{position:relative;z-index:0;display:block;font:var(--tui-font-text-m);border-radius:var(--tui-radius-m);max-height:inherit;min-height:10rem;box-sizing:border-box}.t-wrapper{display:flex;max-height:inherit;min-height:inherit;cursor:text;overflow:hidden;flex-direction:column;border-radius:inherit}.t-placeholder{transition-property:transform,font-size,color,letter-spacing;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;display:block;width:100%;-webkit-user-select:none;-moz-user-select:none;user-select:none;font:var(--tui-font-text-s);color:var(--tui-text-02);pointer-events:none;will-change:transform;transform:translateY(0);margin:1rem 1rem -1rem;min-height:1.625rem;width:95%;font-size:.9375rem}.t-placeholder_raised{transform:translateY(-.625rem)}:host[data-size=m] .t-placeholder_raised{font:var(--tui-font-text-xs);line-height:1.25rem;transform:translateY(-.5rem);letter-spacing:.025rem}:host._invalid:not(._focused) .t-placeholder_raised,:host._invalid:not(._focused):hover .t-placeholder_raised{color:var(--tui-error-fill)}:host._invalid:not(._focused) [tuiWrapper][data-mode=onDark] .t-placeholder_raised,:host._invalid:not(._focused):hover [tuiWrapper][data-mode=onDark] .t-placeholder_raised{color:var(--tui-error-fill-night)}:host._focused .t-placeholder,:host[data-size=m]._focused._label-outside .t-placeholder,:host[data-size=l]._focused._label-outside .t-placeholder{color:var(--tui-text-03)}:host[data-size=l] .t-placeholder{font:var(--tui-font-text-m);line-height:1.25rem}:host[data-size=l] .t-placeholder_raised{font-size:.8156rem}:host[data-size=m]._focused:not(._label-outside) .t-placeholder,:host[data-size=l]._focused:not(._label-outside) .t-placeholder{color:var(--tui-text-01)}[tuiWrapper][data-mode=onDark] .t-placeholder{color:var(--tui-text-02-night)}:host[data-size=m]._focused:not(._label-outside) [tuiWrapper][data-mode=onDark] .t-placeholder,:host[data-size=l]._focused:not(._label-outside) [tuiWrapper][data-mode=onDark] .t-placeholder{color:var(--tui-text-01-night)}:host._focused [tuiWrapper][data-mode=onDark] .t-placeholder,:host[data-size=m]._focused._label-outside [tuiWrapper][data-mode=onDark] .t-placeholder,:host[data-size=l]._focused._label-outside [tuiWrapper][data-mode=onDark] .t-placeholder{color:var(--tui-text-02-night)}@supports (-webkit-hyphens: none){.t-placeholder{will-change:unset;transition-property:transform,color,letter-spacing}}.t-placeholder_raised{width:110%}.t-placeholder:empty{min-height:.8rem}.t-example-text{position:absolute;left:1rem;margin-top:1.6rem;color:var(--tui-base-06);letter-spacing:normal;text-transform:none}.t-example-text_light{color:var(--tui-text-03-night)}.t-example-text_dark{color:var(--tui-text-03)}.t-scrollbar{display:flex;border-bottom:.3125rem solid transparent;padding-bottom:1.3125rem;box-sizing:border-box;flex:1}\n"], components: [{ type: i1.TuiToolbarComponent, selector: "tui-toolbar", inputs: ["colors", "disabled", "tools"], outputs: ["linkAdded", "texClicked", "fileAttached"] }, { type: i2.TuiScrollbarComponent, selector: "tui-scrollbar", inputs: ["hidden"] }, { type: i3.TuiEditorSocketComponent, selector: "tui-editor-socket", inputs: ["content"] }, { type: i4.TuiEditLinkComponent, selector: "tui-edit-link", inputs: ["anchorMode"], outputs: ["addLink", "removeLink"] }, { type: TuiEditorPortalHostComponent, selector: "tui-editor-portal-host" }], directives: [{ type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.TuiWrapperDirective, selector: "[tuiWrapper]", inputs: ["disabled", "readOnly", "hover", "active", "focus", "invalid", "appearance"] }, { type: i7.TuiActiveZoneDirective, selector: "[tuiActiveZone]:not(ng-container), [tuiActiveZoneChange]:not(ng-container), [tuiActiveZoneParent]:not(ng-container)", inputs: ["tuiActiveZoneParent"], outputs: ["tuiActiveZoneChange"], exportAs: ["tuiActiveZone"] }, { type: TuiEditorPortalDirective, selector: "[tuiEditorPortal]" }, { type: i2.TuiDropdownOptionsDirective, selector: "[tuiDropdownAlign], [tuiDropdownAppearance], [tuiDropdownDirection], [tuiDropdownLimitWidth], [tuiDropdownMinHeight], [tuiDropdownMaxHeight], [tuiDropdownOffset]", inputs: ["tuiDropdownAlign", "tuiDropdownAppearance", "tuiDropdownDirection", "tuiDropdownLimitWidth", "tuiDropdownMinHeight", "tuiDropdownMaxHeight", "tuiDropdownOffset"] }, { type: i2.TuiDropdownDirective, selector: "[tuiDropdown]:not(ng-container)", inputs: ["tuiDropdown"], exportAs: ["tuiDropdown"] }, { type: i2.TuiDropdownDriverDirective, selector: "[tuiDropdown]" }, { type: i2.TuiDropdownPositionDirective, selector: "[tuiDropdown]:not([tuiDropdownCustomPosition]):not([tuiDropdownSided])" }, { type: i2.TuiDropdownSelectionDirective, selector: "[tuiDropdown][tuiDropdownSelection]", inputs: ["tuiDropdownSelectionPosition", "tuiDropdownSelection"] }, { type: i9.TuiTiptapEditorDirective, selector: "[tuiTiptapEditor]", inputs: ["value", "editable"], outputs: ["valueChange", "stateChange"] }], pipes: { "async": i6.AsyncPipe }, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-editor',
                    templateUrl: './editor.component.html',
                    styleUrls: ['./editor.component.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [
                        tuiAsFocusableItemAccessor(TuiEditorComponent),
                        tuiAutoFocusOptionsProvider({ delay: TUI_ANIMATIONS_DEFAULT_DURATION }),
                        TUI_EDITOR_PROVIDERS,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i10.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NgControl]
                }] }, { type: i0.ChangeDetectorRef, decorators: [{
                    type: Inject,
                    args: [ChangeDetectorRef]
                }] }, { type: i11.Observable, decorators: [{
                    type: Inject,
                    args: [TIPTAP_EDITOR]
                }] }, { type: i12.AbstractTuiEditor, decorators: [{
                    type: Inject,
                    args: [TuiTiptapEditorService]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_CONTENT_PROCESSOR]
                }] }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i7.AbstractTuiValueTransformer, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TUI_EDITOR_VALUE_TRANSFORMER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_EDITOR_OPTIONS]
                }] }]; }, propDecorators: { el: [{
                type: ViewChild,
                args: [TuiTiptapEditorDirective, { read: ElementRef }]
            }], exampleText: [{
                type: Input
            }], tools: [{
                type: Input
            }], fileAttached: [{
                type: Output
            }], toolbar: [{
                type: ViewChild,
                args: [TuiToolbarComponent]
            }] } });

class TuiEditorModule {
}
TuiEditorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiEditorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorModule, declarations: [TuiEditorComponent,
        TuiEditorPortalHostComponent,
        TuiEditorPortalDirective], imports: [CommonModule,
        TuiToolbarModule,
        TuiWrapperModule,
        TuiScrollbarModule,
        TuiEditLinkModule,
        TuiActiveZoneModule,
        TuiDropdownModule,
        TuiTiptapEditorModule,
        TuiEditorSocketModule], exports: [TuiEditorComponent, TuiItemDirective] });
TuiEditorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorModule, imports: [[
            CommonModule,
            TuiToolbarModule,
            TuiWrapperModule,
            TuiScrollbarModule,
            TuiEditLinkModule,
            TuiActiveZoneModule,
            TuiDropdownModule,
            TuiTiptapEditorModule,
            TuiEditorSocketModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiEditorModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        TuiEditorComponent,
                        TuiEditorPortalHostComponent,
                        TuiEditorPortalDirective,
                    ],
                    imports: [
                        CommonModule,
                        TuiToolbarModule,
                        TuiWrapperModule,
                        TuiScrollbarModule,
                        TuiEditLinkModule,
                        TuiActiveZoneModule,
                        TuiDropdownModule,
                        TuiTiptapEditorModule,
                        TuiEditorSocketModule,
                    ],
                    exports: [TuiEditorComponent, TuiItemDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_EDITOR_PROVIDERS, TuiEditorComponent, TuiEditorModule, TuiEditorPortalDirective, TuiEditorPortalHostComponent, TuiEditorPortalService };
